/**
 * Login Page — Professional split (desktop) + lifted-card hero (mobile).
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Shield, Users, BarChart3, Receipt, Lock, ArrowRight, CheckCircle2, Sparkles,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FEATURES = [
  { icon: BarChart3, title: 'Kewangan Real-time', desc: 'Revenue, Expenses, Cashflow & P&L dalam satu dashboard' },
  { icon: Users, title: 'HR & Payroll Lengkap', desc: 'Auto-calculate EPF/SOCSO/EIS/PCB ikut LHDN' },
  { icon: Receipt, title: 'AI Receipt Scanner', desc: 'Tuntutan belanja auto-extract dengan Gemini AI' },
  { icon: Shield, title: 'LHDN Compliant', desc: 'EA Form, e-Invoice, Borang E — semua siap' },
];

// Reusable feature card (used by both mobile compact + desktop)
const FeatureCard = ({ icon: Icon, title, desc, variant = 'dark' }) => {
  const isDark = variant === 'dark';
  return (
    <div
      className={`group flex items-start gap-3 p-3 rounded-xl border transition-all duration-300 ${
        isDark
          ? 'bg-white/5 backdrop-blur-sm border-white/10 hover:bg-white/10 hover:border-white/20'
          : 'bg-white border-gray-100 hover:border-red-200 hover:shadow-md'
      }`}
    >
      <div
        className={`shrink-0 w-9 h-9 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform ${
          isDark ? 'bg-amber-400/20 border border-amber-300/30' : 'bg-red-50 border border-red-100'
        }`}
      >
        <Icon className={`w-4 h-4 ${isDark ? 'text-amber-300' : 'text-red-700'}`} />
      </div>
      <div className="min-w-0">
        <p className={`text-sm font-semibold leading-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>{title}</p>
        <p className={`text-xs leading-snug mt-0.5 ${isDark ? 'text-red-200/70' : 'text-gray-500'}`}>{desc}</p>
      </div>
    </div>
  );
};

const LoginPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Build headers — attach Bearer token from localStorage as Safari ITP fallback
        const headers = {};
        try {
          const token = localStorage.getItem('ua_session_token');
          if (token) headers.Authorization = `Bearer ${token}`;
        } catch { /* private mode */ }
        const response = await fetch(`${API_URL}/api/auth/me`, {
          credentials: 'include',
          headers,
        });
        if (response.ok) navigate('/');
      } catch { /* not authenticated */ }
    };
    checkAuth();
  }, [navigate]);

  const handleGoogleLogin = () => {
    setLoading(true);
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // Shared dot-pattern style for red panels
  const dotPatternStyle = {
    backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
    backgroundSize: '32px 32px',
  };

  return (
    <div className="min-h-screen bg-white flex flex-col lg:flex-row" data-testid="login-page">
      {/* ==========================================================
          MOBILE — Minimalist single-column login
          ========================================================== */}
      <main className="lg:hidden flex flex-col min-h-screen px-6 py-12 relative overflow-hidden">
        {/* Subtle red orb at top — only visual interest */}
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-72 h-72 bg-red-100 rounded-full blur-3xl opacity-60 pointer-events-none" />

        {/* Brand mark — top */}
        <div className="relative z-10 flex flex-col items-center pt-8 animate-fade-in-down">
          <div className="w-16 h-16 bg-gradient-to-br from-red-700 to-red-900 text-white rounded-2xl flex items-center justify-center font-black text-2xl tracking-tight shadow-xl shadow-red-900/20">
            UA
          </div>
          <p className="mt-4 text-base font-bold text-gray-900 tracking-tight">UA Syarikat</p>
          <p className="text-[11px] text-gray-400 tracking-widest uppercase mt-0.5">Underads Sdn Bhd</p>
        </div>

        {/* Spacer */}
        <div className="flex-1 min-h-[40px]" />

        {/* Login section — centered focal point */}
        <div className="relative z-10 max-w-sm mx-auto w-full space-y-8 animate-fade-in">
          <div className="text-center space-y-2">
            <h1 className="text-3xl font-black text-gray-900 tracking-tight">Selamat datang.</h1>
            <p className="text-gray-500 text-sm">Log masuk untuk akses dashboard.</p>
          </div>

          <button
            onClick={handleGoogleLogin}
            disabled={loading}
            data-testid="google-login-btn-mobile"
            className="group w-full flex items-center justify-center gap-3 px-5 py-3.5 bg-gray-900 hover:bg-gray-800 text-white rounded-2xl font-semibold text-sm transition-all duration-200 shadow-lg shadow-gray-900/15 active:scale-[0.98] disabled:opacity-60"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            <span>{loading ? 'Memproses…' : 'Log masuk dengan Google'}</span>
            {!loading && <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />}
          </button>

          {/* Single subtle trust hint */}
          <p className="text-center text-[11px] text-gray-400 leading-relaxed">
            <Lock className="w-3 h-3 inline-block mr-1 -mt-0.5" />
            Akses terhad untuk team Underads sahaja.
          </p>
        </div>

        {/* Spacer */}
        <div className="flex-1 min-h-[40px]" />

        {/* Footer — minimal */}
        <footer className="relative z-10 text-center space-y-1 pt-6">
          <p className="text-[10px] font-bold tracking-[0.3em] text-gray-400 uppercase">
            Powered by <span className="text-red-700">Underads</span>
          </p>
          <p className="text-[10px] text-gray-300">v2.0 • © {new Date().getFullYear()}</p>
        </footer>
      </main>

      {/* ==========================================================
          DESKTOP — LEFT BRAND PANEL (hidden on mobile)
          ========================================================== */}
      <aside className="relative hidden lg:flex lg:w-[58%] xl:w-[60%] flex-col justify-between overflow-hidden bg-gradient-to-br from-red-950 via-red-900 to-red-800 p-12 xl:p-16 text-white">
        <div className="absolute inset-0 opacity-[0.07] pointer-events-none" style={dotPatternStyle} />
        <div className="absolute -top-24 -left-24 w-96 h-96 bg-red-500/20 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-24 right-12 w-[500px] h-[500px] bg-amber-500/10 rounded-full blur-3xl pointer-events-none" />

        <div className="relative z-10 animate-fade-in-down">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="w-14 h-14 bg-white text-red-800 rounded-2xl flex items-center justify-center font-black text-2xl tracking-tight shadow-lg shadow-red-950/40">
                UA
              </div>
              <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-amber-400 rounded-full border-2 border-red-900 flex items-center justify-center">
                <Sparkles className="w-3 h-3 text-red-900" />
              </div>
            </div>
            <div>
              <p className="text-xl font-bold tracking-tight">UA Syarikat</p>
              <p className="text-xs text-red-200/80 tracking-wider uppercase">Underads Sdn Bhd</p>
            </div>
          </div>
        </div>

        <div className="relative z-10 max-w-xl space-y-6 animate-fade-in-up">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full text-xs font-medium text-amber-200">
            <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse" />
            Sistem Aktif • LHDN Compliant • PWA Ready
          </div>
          <h1 className="text-4xl xl:text-5xl font-black leading-[1.1] tracking-tight">
            Pengurusan Kewangan & HR
            <span className="block text-amber-300 mt-2">tanpa kerumitan.</span>
          </h1>
          <p className="text-base xl:text-lg text-red-100/90 leading-relaxed max-w-lg">
            Satu sistem untuk seluruh syarikat — dari real-time finance dashboard ke payroll auto-LHDN, attendance GPS, sampai AI receipt scanner.
          </p>

          <div className="grid grid-cols-2 gap-3 pt-4">
            {FEATURES.map((f) => (
              <FeatureCard key={f.title} {...f} variant="dark" />
            ))}
          </div>
        </div>

        <div className="relative z-10 flex items-center gap-6 pt-6 border-t border-white/10">
          <div>
            <p className="text-2xl font-black text-amber-300">240+</p>
            <p className="text-[11px] text-red-200/70 uppercase tracking-wider">Tests Pass</p>
          </div>
          <div className="h-10 w-px bg-white/10" />
          <div>
            <p className="text-2xl font-black text-amber-300">100%</p>
            <p className="text-[11px] text-red-200/70 uppercase tracking-wider">LHDN Ready</p>
          </div>
          <div className="h-10 w-px bg-white/10" />
          <div>
            <p className="text-2xl font-black text-amber-300">PWA</p>
            <p className="text-[11px] text-red-200/70 uppercase tracking-wider">Mobile App</p>
          </div>
        </div>
      </aside>

      {/* ==========================================================
          DESKTOP — RIGHT FORM PANEL (hidden on mobile)
          ========================================================== */}
      <section className="hidden lg:flex flex-1 flex-col justify-center px-12 xl:px-20 py-16 relative">
        <div className="w-full max-w-md space-y-8 animate-fade-in">
          <div className="space-y-2">
            <p className="text-sm font-medium text-red-700 uppercase tracking-widest">Welcome back</p>
            <h2 className="text-3xl xl:text-4xl font-black text-gray-900 tracking-tight">Selamat Datang.</h2>
            <p className="text-gray-500 text-base">
              Log masuk untuk akses dashboard kewangan & HR syarikat anda.
            </p>
          </div>

          <button
            onClick={handleGoogleLogin}
            disabled={loading}
            data-testid="google-login-btn"
            className="group relative w-full flex items-center justify-center gap-3 px-5 py-4 bg-gray-900 hover:bg-gray-800 text-white rounded-xl font-medium transition-all duration-200 shadow-lg shadow-gray-900/20 hover:shadow-xl hover:shadow-gray-900/30 hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            <span className="font-semibold">{loading ? 'Memproses…' : 'Log masuk dengan Google'}</span>
            {!loading && <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />}
          </button>

          <ul className="space-y-2.5 pt-2">
            <li className="flex items-center gap-3 text-sm text-gray-600">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
              <span>Secure Google OAuth — tiada password disimpan</span>
            </li>
            <li className="flex items-center gap-3 text-sm text-gray-600">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
              <span>Role-based access (Director / Finance / HR / Staff)</span>
            </li>
            <li className="flex items-center gap-3 text-sm text-gray-600">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
              <span>Akses terhad untuk team Underads sahaja</span>
            </li>
          </ul>

          <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
            <Lock className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
            <p className="text-xs text-amber-900 leading-relaxed">
              <span className="font-semibold">Akses Terhad.</span> Akaun Google anda mesti telah didaftarkan dalam sistem oleh admin HR. Hubungi admin jika anda menghadapi masalah log masuk.
            </p>
          </div>
        </div>

        <footer className="absolute bottom-6 left-12 right-12 xl:left-20 xl:right-20">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-2">
            <p className="text-[11px] font-bold tracking-[0.25em] text-gray-600 uppercase">
              Powered by <span className="text-red-700">Underads</span>
            </p>
            <div className="text-[11px] text-gray-400 text-center sm:text-right">
              <p>© {new Date().getFullYear()} Underads Sdn Bhd • v2.0 • Sistem Pengurusan Kewangan & HR</p>
            </div>
          </div>
        </footer>
      </section>
    </div>
  );
};

export default LoginPage;
